(() => {
  const GRID_SELECTOR = '[data-editorial-collection-grid]';
  const TILE_SELECTOR = '[data-editorial-tile]';
  const TWO_BY_ONE_SELECTOR = '[data-editorial-tile][data-editorial-size="2x1"]';
  const TWO_BY_TWO_SELECTOR = '[data-editorial-tile][data-editorial-size="2x2"]';
  const PRODUCT_SELECTOR = '.product-item:not(.product-item--featured-image)';
  const IMAGE_SELECTOR = '[data-product-image], .product-item__image';
  const INFO_SELECTOR = '.product-item__info';
  const TOLERANCE = 3;

  class EditorialCollectionGrid {
    constructor(grid) {
      this.grid = grid;
      this.raf = null;
      this.resizeTimer = null;
      this.mutationObserver = new MutationObserver(() => this.schedule());
      this.onResize = this.onResize.bind(this);
      this.init();
    }

    init() {
      /*
       * Observe inserted/replaced grid children only. We deliberately avoid a
       * ResizeObserver because changing a 2x2 tile's height can resize the grid
       * and create a feedback loop in Shopify's animated device preview.
       */
      this.mutationObserver.observe(this.grid, { childList: true, subtree: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onResize, { passive: true });

      this.bindPendingImages();
      this.schedule();
      setTimeout(() => this.schedule(), 120);
    }

    bindPendingImages() {
      this.grid.querySelectorAll('img').forEach((image) => {
        if (!image.complete && !image.dataset.editorialGridLoadBound) {
          image.dataset.editorialGridLoadBound = 'true';
          image.addEventListener('load', () => this.schedule(), { once: true });
        }
      });
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.schedule();

      /* Re-run after Shopify's desktop/mobile preview transition settles. */
      this.resizeTimer = setTimeout(() => this.schedule(), 220);
    }

    schedule() {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => this.update());
      });
    }

    getColumnCount() {
      const template = getComputedStyle(this.grid).gridTemplateColumns;
      if (!template || template === 'none') return 1;
      return Math.max(1, template.split(/\s+/).filter(Boolean).length);
    }

    applyTilePlacement(columns) {
      this.grid.querySelectorAll(TILE_SELECTOR).forEach((tile) => {
        const size = tile.dataset.editorialSize || '1x1';
        const wantsTwoColumns = size === '2x1' || size === '2x2';
        const columnSpan = wantsTwoColumns && columns >= 2 ? 2 : 1;
        const rowSpan = size === '2x2' ? 2 : 1;

        /* Inline important styles make placement deterministic even if a
         * Broadcast rule or Theme Editor re-render wins the stylesheet order. */
        tile.style.setProperty('grid-column', `span ${columnSpan}`, 'important');
        tile.style.setProperty('grid-row', `span ${rowSpan}`, 'important');

        if (size !== '2x1') {
          tile.style.removeProperty('--editorial-2x1-height');
        }

        if (size !== '2x2') {
          tile.style.removeProperty('--editorial-2x2-height');
        }
      });
    }

    getProductData() {
      const products = [...this.grid.querySelectorAll(PRODUCT_SELECTOR)];

      return products
        .map((product) => {
          const image = product.querySelector(IMAGE_SELECTOR);
          const info = product.querySelector(INFO_SELECTOR);
          const rect = product.getBoundingClientRect();
          const imageHeight = image ? image.getBoundingClientRect().height : 0;

          return {
            top: rect.top,
            imageHeight,
            infoHeight: info
              ? info.getBoundingClientRect().height
              : Math.max(0, rect.height - imageHeight),
          };
        })
        .filter((item) => item.imageHeight > 0);
    }

    getReferenceRow(tile, productData) {
      const tileTop = tile.getBoundingClientRect().top;
      let referenceRow = productData.filter(
        (item) => Math.abs(item.top - tileTop) <= TOLERANCE
      );

      if (!referenceRow.length) {
        const nearestTop = productData.reduce((nearest, item) => {
          return Math.abs(item.top - tileTop) < Math.abs(nearest - tileTop)
            ? item.top
            : nearest;
        }, productData[0].top);

        referenceRow = productData.filter(
          (item) => Math.abs(item.top - nearestTop) <= TOLERANCE
        );
      }

      return referenceRow;
    }

    updateTwoByOneHeights(productData) {
      const tiles = [...this.grid.querySelectorAll(TWO_BY_ONE_SELECTOR)];
      if (!tiles.length || !productData.length) return;

      tiles.forEach((tile) => {
        const referenceRow = this.getReferenceRow(tile, productData);
        const reference = referenceRow[0] || productData[0];
        const nextValue = Math.round(reference.imageHeight * 100) / 100;
        const currentValue =
          parseFloat(tile.style.getPropertyValue('--editorial-2x1-height')) || 0;

        if (Math.abs(currentValue - nextValue) > 0.5) {
          tile.style.setProperty('--editorial-2x1-height', `${nextValue}px`);
        }
      });
    }

    updateTwoByTwoHeights(productData) {
      const tiles = [...this.grid.querySelectorAll(TWO_BY_TWO_SELECTOR)];
      if (!tiles.length) return;

      if (!productData.length) return;

      const gridStyles = getComputedStyle(this.grid);
      const rowGap = parseFloat(gridStyles.rowGap || gridStyles.gap) || 0;

      tiles.forEach((tile) => {
        const referenceRow = this.getReferenceRow(tile, productData);

        const reference = referenceRow[0] || productData[0];
        const firstRowInfoHeight = Math.max(
          ...referenceRow.map((item) => item.infoHeight),
          reference.infoHeight,
          0
        );
        const targetHeight =
          reference.imageHeight * 2 + firstRowInfoHeight + rowGap;
        const nextValue = Math.round(targetHeight * 100) / 100;
        const currentValue =
          parseFloat(tile.style.getPropertyValue('--editorial-2x2-height')) || 0;

        if (Math.abs(currentValue - nextValue) > 0.5) {
          tile.style.setProperty('--editorial-2x2-height', `${nextValue}px`);
        }
      });
    }

    update() {
      if (!this.grid.isConnected) return;

      this.bindPendingImages();
      this.applyTilePlacement(this.getColumnCount());

      const productData = this.getProductData();
      this.updateTwoByOneHeights(productData);
      this.updateTwoByTwoHeights(productData);
    }
  }

  const instances = new WeakMap();

  const initialise = (root = document) => {
    const grids = root.matches?.(GRID_SELECTOR)
      ? [root]
      : [...root.querySelectorAll(GRID_SELECTOR)];

    grids.forEach((grid) => {
      if (instances.has(grid)) {
        instances.get(grid).schedule();
        return;
      }

      instances.set(grid, new EditorialCollectionGrid(grid));
    });
  };

  document.addEventListener('DOMContentLoaded', () => initialise());
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:reorder', () => initialise());
  document.addEventListener('shopify:block:select', (event) => {
    initialise(event.target.closest(GRID_SELECTOR) || document);
  });
  document.addEventListener('shopify:block:deselect', () => initialise());

  /* Broadcast replaces the product grid after filtering and sorting. */
  const pageObserver = new MutationObserver((mutations) => {
    const hasNewGrid = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.(GRID_SELECTOR) || node.querySelector?.(GRID_SELECTOR))
      )
    );

    if (hasNewGrid) initialise();
  });

  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
